/**
 * RS / Packaging — furoshiki tie/untie cycle.
 *
 * A few seconds after arriving, the pack unties and the cards fan out; after
 * the open pause it ties itself again, in a continuous cycle (or opens once,
 * with Loop off). Timers run ONLY while the section is inside the
 * (optionally anticipated) viewport — leaving it clears them, re-entering
 * resumes the cycle. Nothing ever runs off screen.
 */
class RsPackaging extends HTMLElement {
  #observer = null;
  #timer = null;
  #startedOnce = false;

  connectedCallback() {
    this.knotLabel = this.querySelector('[data-rs-knot-label]');

    if (this.dataset.animate !== 'true') {
      this.#setTied(false);
      return;
    }

    this.initialDelay = (parseFloat(this.dataset.initialDelay) || 0) * 1000;
    this.pauseOpen = (parseFloat(this.dataset.pauseOpen) || 4) * 1000;
    this.loop = this.dataset.loop === 'true';
    const advance = parseFloat(this.dataset.revealAdvance) || 0;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#schedule(this.#startedOnce ? 400 : this.initialDelay, false);
          else this.#halt();
        }
      },
      { threshold: 0, rootMargin: `0px 0px ${advance}% 0px` }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#halt();
    this.#observer?.disconnect();
  }

  /* One pending step at a time: open after the wait, then either stay open
     (no loop) or tie again after the pause and start over */
  #schedule(waitMs, tie) {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => {
      this.#startedOnce = true;
      this.#setTied(tie);
      if (tie) {
        this.#schedule(this.pauseOpen * 0.6, false);
      } else if (this.loop) {
        this.#schedule(this.pauseOpen, true);
      }
    }, waitMs);
  }

  #halt() {
    clearTimeout(this.#timer);
    this.#timer = null;
  }

  #setTied(tied) {
    if (this.classList.contains('is-tied') === tied) return;
    this.classList.toggle('is-tied', tied);
    if (this.knotLabel) {
      this.knotLabel.textContent = tied ? this.dataset.labelTied : this.dataset.labelUntied;
    }
  }
}

if (!customElements.get('rs-packaging')) {
  customElements.define('rs-packaging', RsPackaging);
}
